import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  Shield, 
  Clock, 
  Users, 
  CheckCircle, 
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  Play,
  ChevronLeft,
  ChevronRight,
  Send,
  Target,
  Eye,
  Handshake,
  FileCheck,
  Home,
  Scale,
  PiggyBank,
  Award,
  HelpCircle,
  Lightbulb,
  Calculator,
  Banknote,
  Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping for dynamic content
const iconMap = {
  Shield, Phone, TrendingUp, CheckCircle, Lightbulb, Calculator, Banknote,
  Handshake, Award, PiggyBank, Home, FileCheck, Clock, Users, Target, Eye, Scale, HelpCircle
};

const getIcon = (iconName) => iconMap[iconName] || Shield;

// Logo Component
const Logo = ({ size = "default", className = "" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_552d4860-4146-493e-8e68-213c46e43a66/artifacts/eqpwekmt_Nettrix%20Logo.png"
    alt="Nettrix Invest"
    className={`${size === "large" ? "h-[94px] md:h-[125px]" : "h-[78px] md:h-[94px]"} w-auto ${className}`}
    data-testid="logo"
  />
);

// WhatsApp Icon
const WhatsAppIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Instagram Icon
const InstagramIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

// Navigation Component
const Navigation = ({ scrollToSection }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const navItems = [
    { label: 'How It Works?', section: 'how-it-works' },
    { label: 'About Us', section: 'about-us' },
    { label: 'Our Plans', section: 'our-plans' },
    { label: 'Why Choose Us', section: 'why-choose-us' },
    { label: 'How to Start?', section: 'how-to-start' },
    { label: 'Contact', section: 'contact' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-gray-200 shadow-sm" data-testid="navigation">
      <div className="nettrix-container">
        <div className="flex items-center justify-between h-24 md:h-28">
          <Logo size="large" />
          <div className="hidden lg:flex items-center gap-6">
            {navItems.map((item) => (
              <button key={item.section} onClick={() => scrollToSection(item.section)} className="nav-link text-gray-600 hover:text-[#1A1A1A] text-sm font-semibold tracking-wide transition-colors" data-testid={`nav-${item.section}`}>
                {item.label}
              </button>
            ))}
            <Button onClick={() => scrollToSection('contact')} className="bg-[#D4AF37] text-black hover:bg-[#F5C518] font-bold px-6 py-2 rounded-sm tracking-wider btn-gold-glow" data-testid="nav-cta">
              INVEST NOW
            </Button>
          </div>
          <button className="lg:hidden text-[#1A1A1A] p-2" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-toggle">
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="lg:hidden bg-white border-t border-gray-100">
            <div className="nettrix-container py-4 flex flex-col gap-4">
              {navItems.map((item) => (
                <button key={item.section} onClick={() => { scrollToSection(item.section); setIsOpen(false); }} className="text-gray-600 hover:text-[#1A1A1A] text-left py-2 text-base font-medium">
                  {item.label}
                </button>
              ))}
              <Button onClick={() => { scrollToSection('contact'); setIsOpen(false); }} className="bg-[#D4AF37] text-black hover:bg-[#F5C518] font-bold w-full mt-2">
                INVEST NOW
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Urgency Meter Component - Percentage Only
const UrgencyMeter = ({ data }) => {
  const [animatedPercent, setAnimatedPercent] = useState(0);
  
  const percent = data?.percentage || 60;
  const monthLabel = data?.month_label || 'This Month';
  
  useEffect(() => {
    const timer = setTimeout(() => setAnimatedPercent(percent), 500);
    return () => clearTimeout(timer);
  }, [percent]);

  // Show "Goal Reached" message when 100%
  if (percent >= 100) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }} className="relative" data-testid="urgency-meter">
        <div className="relative w-64 h-64 md:w-80 md:h-80 mx-auto">
          <div className="absolute inset-0 rounded-full" style={{ boxShadow: '0 0 60px rgba(212, 175, 55, 0.3)' }} />
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="45" fill="none" stroke="#E5E5E5" strokeWidth="6" />
            <motion.circle cx="50" cy="50" r="45" fill="none" stroke="url(#goldGradient)" strokeWidth="6" strokeLinecap="round" strokeDasharray="283 283" initial={{ strokeDasharray: '0 283' }} animate={{ strokeDasharray: '283 283' }} transition={{ duration: 1.5, ease: 'easeOut' }} />
            <defs><linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor="#D4AF37" /><stop offset="50%" stopColor="#F5C518" /><stop offset="100%" stopColor="#D4AF37" /></linearGradient></defs>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <span className="text-[#D4AF37] text-4xl mb-2">✓</span>
            <span className="text-2xl md:text-3xl font-heading font-bold text-[#D4AF37]">100%</span>
            <span className="text-gray-600 text-sm mt-2">Goal Reached!</span>
          </div>
        </div>
        <motion.div className="mt-8 bg-[#D4AF37] px-6 py-4 rounded-lg shadow-lg max-w-xs mx-auto text-center" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 1 }}>
          <p className="text-black font-bold text-sm">We've reached our funding goal for {monthLabel}!</p>
          <p className="text-black/70 text-xs mt-1">Please wait for next month's opportunity.</p>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }} className="relative" data-testid="urgency-meter">
      <div className="relative w-64 h-64 md:w-80 md:h-80 mx-auto">
        <div className="absolute inset-0 rounded-full" style={{ boxShadow: '0 0 60px rgba(212, 175, 55, 0.2)' }} />
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="45" fill="none" stroke="#E5E5E5" strokeWidth="6" />
          <motion.circle cx="50" cy="50" r="45" fill="none" stroke="url(#goldGradient)" strokeWidth="6" strokeLinecap="round" strokeDasharray={`${animatedPercent * 2.83} 283`} initial={{ strokeDasharray: '0 283' }} animate={{ strokeDasharray: `${animatedPercent * 2.83} 283` }} transition={{ duration: 1.5, ease: 'easeOut' }} />
          <defs><linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor="#D4AF37" /><stop offset="50%" stopColor="#F5C518" /><stop offset="100%" stopColor="#D4AF37" /></linearGradient></defs>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-gray-500 text-sm uppercase tracking-widest mb-2">{monthLabel}</span>
          <span className="text-4xl md:text-5xl font-heading font-bold text-[#D4AF37]">{Math.round(animatedPercent)}%</span>
          <span className="text-gray-500 text-sm mt-2">Funding Complete</span>
        </div>
      </div>
    </motion.div>
  );
};

// Hero Section
const HeroSection = ({ urgencyData, scrollToSection, content }) => {
  const hero = content?.hero || {};
  
  return (
    <section id="hero" className="min-h-screen bg-white flex items-center pt-28" data-testid="hero-section">
      <div className="nettrix-container py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
            <span className="text-[#D4AF37] text-sm uppercase tracking-[0.3em] font-semibold mb-4 block">
              {hero.tagline || 'Premium Investment Opportunity'}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1A1A1A] leading-tight mb-6">
              {hero.headline?.split(hero.highlight_text || '30% Returns')[0]}
              <span className="text-[#D4AF37]">{hero.highlight_text || '30% Returns'}</span>
              {hero.headline?.split(hero.highlight_text || '30% Returns')[1]}
            </h1>
            <p className="text-gray-600 text-lg md:text-xl leading-relaxed mb-8 max-w-xl">
              {hero.description || 'Nettrix Invest offers short-term debt funds with guaranteed monthly payouts of 5% or 30% fixed returns after 6 months. Limited slots available each month.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={() => scrollToSection('contact')} className="bg-[#D4AF37] text-black hover:bg-[#F5C518] font-bold px-8 py-6 rounded-sm tracking-wider text-base btn-gold-glow" data-testid="hero-cta">
                {hero.cta_button_text || 'START INVESTING'} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button onClick={() => scrollToSection('our-plans')} variant="outline" className="border-[#1A1A1A] text-[#1A1A1A] hover:bg-gray-100 px-8 py-6 rounded-sm tracking-wider text-base" data-testid="hero-plans-btn">
                {hero.secondary_button_text || 'VIEW PLANS'}
              </Button>
            </div>
            <div className="mt-10 flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2 text-gray-600"><Shield className="w-5 h-5 text-[#D4AF37]" /><span className="text-sm font-medium">{hero.trust_badge_1 || 'Secure Investment'}</span></div>
              <div className="flex items-center gap-2 text-gray-600"><Clock className="w-5 h-5 text-[#D4AF37]" /><span className="text-sm font-medium">{hero.trust_badge_2 || '7 Years in Business'}</span></div>
              <div className="flex items-center gap-2 text-gray-600"><Award className="w-5 h-5 text-[#D4AF37]" /><span className="text-sm font-medium">{hero.trust_badge_3 || '100% Satisfaction'}</span></div>
            </div>
          </motion.div>
          <div className="flex justify-center lg:justify-end"><UrgencyMeter data={urgencyData} /></div>
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = ({ content }) => {
  const data = content?.how_it_works || {};
  const defaultItems = [
    { title: 'Understanding Our Model', description: 'We raise short-term debt funds from individual investors every month and deploy them in secured lending opportunities with fixed returns.', icon: 'Lightbulb' },
    { title: 'Your Investment Options', description: 'Choose between 5% monthly returns credited to your account or 30% lump sum returns at the end of 6 months.', icon: 'Calculator' },
    { title: 'Security & Documentation', description: 'Every investment is backed by proper legal documentation, post-dated cheques, and transparent agreements.', icon: 'Shield' },
    { title: 'Returns Distribution', description: 'Monthly returns are credited directly to your bank account by the 7th of each month.', icon: 'Banknote' },
  ];
  const items = data.items?.length > 0 ? data.items : defaultItems;

  return (
    <section id="how-it-works" className="py-24 md:py-32 bg-gray-50" data-testid="how-it-works-section">
      <div className="nettrix-container">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="font-heading text-3xl md:text-5xl font-medium text-[#1A1A1A] mb-4">{data.section_title || 'How It Works?'}</h2>
          <div className="w-16 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">{data.section_subtitle || 'Understand our simple and transparent investment model'}</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {items.map((item, index) => {
            const IconComponent = getIcon(item.icon);
            return (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.15 }} className="bg-white border border-gray-200 rounded-xl p-8 hover:border-[#D4AF37] hover:shadow-lg transition-all duration-300" data-testid={`how-it-works-${index + 1}`}>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-lg bg-[#D4AF37] flex items-center justify-center flex-shrink-0"><IconComponent className="w-7 h-7 text-white" /></div>
                  <div><h3 className="font-heading text-xl font-medium text-[#1A1A1A] mb-3">{item.title}</h3><p className="text-gray-600 text-base leading-relaxed">{item.description}</p></div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// About Us Section
const AboutUsSection = ({ content }) => {
  const data = content?.about || {};
  const visionTags = (data.vision_tags || 'Capital is protected|Decisions are clear|Income is steady').split('|');
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <section id="about-us" className="py-24 md:py-32 bg-white" data-testid="about-us-section">
      <div className="nettrix-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-white border-2 border-gray-200 rounded-xl p-8 md:p-10">
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-[#1A1A1A] mb-6">{data.title || 'About Us'}</h2>
            <p className="text-[#D4AF37] font-semibold text-lg mb-4 italic">{data.tagline || 'Wealth Should Feel Peaceful.'}</p>
            <p className="text-gray-600 text-base leading-relaxed mb-4">{data.description_1 || 'Nettrix Marketing Pvt. Ltd. is a premium wealth advisory firm.'}</p>
            {expandedSection === 'about' ? (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                <p className="text-gray-600 text-base leading-relaxed mb-4">{data.description_2 || 'We design secure, predictable income systems.'}</p>
                <p className="text-gray-600 text-base leading-relaxed mb-6 whitespace-pre-line">{data.description_3 || "We don't chase hype.\nWe don't promise quick returns."}</p>
                {data.about_extra && <p className="text-gray-600 text-base leading-relaxed mb-4">{data.about_extra}</p>}
              </motion.div>
            ) : (
              <p className="text-gray-600 text-base leading-relaxed mb-6 line-clamp-2">{data.description_2 || 'We design secure, predictable income systems.'}</p>
            )}
            <Button 
              onClick={() => toggleSection('about')}
              className="bg-[#D4AF37] text-black hover:bg-[#F5C518] font-bold px-6 py-3"
            >
              {expandedSection === 'about' ? 'Show Less' : 'Know More'}
            </Button>
          </motion.div>
          <div className="space-y-6">
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-white border-2 border-gray-200 rounded-xl p-8">
              <h3 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-4">{data.mission_title || 'Our Mission'}</h3>
              <p className="text-gray-600 text-base leading-relaxed mb-4">{data.mission_text || 'To empower people with secure wealth systems.'}</p>
              {expandedSection === 'mission' && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
                  <p className="text-gray-600 text-base leading-relaxed mb-4">{data.mission_text_2 || 'We exist to bring clarity to personal finance.'}</p>
                  {data.mission_extra && <p className="text-gray-600 text-base leading-relaxed mb-4">{data.mission_extra}</p>}
                </motion.div>
              )}
              <Button 
                onClick={() => toggleSection('mission')}
                variant="outline" 
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 font-bold px-6 py-3"
              >
                {expandedSection === 'mission' ? 'Show Less' : 'Know More'}
              </Button>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="bg-white border-2 border-gray-200 rounded-xl p-8">
              <h3 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-4">{data.vision_title || 'Our Vision'}</h3>
              <p className="text-gray-600 text-base leading-relaxed mb-4">{data.vision_text || "To become India's most trusted name in safe income planning."}</p>
              {expandedSection === 'vision' && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {visionTags.map((tag, i) => (<React.Fragment key={i}>{i > 0 && <span className="text-[#D4AF37]">|</span>}<span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded">{tag}</span></React.Fragment>))}
                  </div>
                  <p className="text-gray-600 text-base leading-relaxed mb-4">{data.vision_text_2 || 'A world where wealth grows without fear.'}</p>
                  {data.vision_extra && <p className="text-gray-600 text-base leading-relaxed mb-4">{data.vision_extra}</p>}
                </motion.div>
              )}
              <Button 
                onClick={() => toggleSection('vision')}
                variant="outline" 
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 font-bold px-6 py-3"
              >
                {expandedSection === 'vision' ? 'Show Less' : 'Know More'}
              </Button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Our Plans Section
const OurPlansSection = ({ content }) => {
  const data = content?.plans || {};
  const defaultPlans = [
    { name: 'Plan 1', return_rate: '30%', return_type: '6 Month Return', description: 'Invest ₹1,00,000 or more. Receive 30% fixed returns after 6 months.', min_amount: '₹1,00,000', bg_color: 'gold' },
    { name: 'Plan 2', return_rate: '5%', return_type: 'Monthly Return', description: 'Invest ₹1,00,000 or more. Receive 5% monthly returns.', min_amount: '₹1,00,000', bg_color: 'dark' },
  ];
  const plans = data.plans?.length > 0 ? data.plans : defaultPlans;

  return (
    <section id="our-plans" className="py-24 md:py-32 bg-gray-50" data-testid="our-plans-section">
      <div className="nettrix-container">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="font-heading text-3xl md:text-5xl font-medium text-[#1A1A1A] mb-4">{data.section_title || 'Payment Plans'}</h2>
          <div className="w-16 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
          <h3 className="font-heading text-2xl text-[#1A1A1A] mb-4">{data.section_subtitle || 'Investment Based'}</h3>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">{data.section_description || 'Start Small. Grow Smooth. Earn Consistently.'}</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.2 }} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all card-hover" data-testid={`plan-${index + 1}`}>
              <div className="p-8 text-center">
                <h4 className="text-[#D4AF37] font-heading text-xl mb-2 underline decoration-[#D4AF37]">Investment Based</h4>
                <span className={`inline-block px-4 py-2 ${plan.bg_color === 'gold' ? 'bg-[#D4AF37]' : 'bg-[#3D3D3D]'} text-white font-bold rounded mb-4`}>{plan.name?.toUpperCase()}</span>
                <div className="text-6xl md:text-7xl font-heading font-bold text-[#1A1A1A]">{plan.return_rate}</div>
              </div>
              <div className={`p-8 ${plan.bg_color === 'gold' ? 'bg-[#D4AF37]' : 'bg-[#3D3D3D]'} text-white`}>
                <h5 className="text-xl font-bold mb-4">{plan.return_type}</h5>
                <p className="text-sm opacity-90 mb-4">{plan.description}</p>
                <p className="text-sm opacity-90 mb-6">Minimum Investment: {plan.min_amount}</p>
                <button className="underline text-sm font-medium hover:no-underline">Start Investing</button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Investment Calculator Section
const InvestmentCalculator = ({ settings }) => {
  const [amount, setAmount] = useState(100000);
  
  // Use settings from API or defaults
  const monthlyRate = settings?.monthly_rate || 5;
  const lumpSumRate = settings?.lump_sum_rate || 30;
  const lumpSumMonths = settings?.lump_sum_months || 6;
  const minInvestment = settings?.min_investment || 100000;
  const maxInvestment = settings?.max_investment || 10000000;
  
  const monthlyReturn = amount * (monthlyRate / 100);
  const lumpSumReturn = amount * (lumpSumRate / 100);
  const totalAfterPeriod = amount + lumpSumReturn;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value);
  };

  return (
    <section id="calculator" className="py-24 md:py-32 bg-white" data-testid="calculator-section">
      <div className="nettrix-container">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="font-heading text-3xl md:text-5xl font-medium text-[#1A1A1A] mb-4">Investment Calculator</h2>
          <div className="w-16 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">See how much you can earn with Nettrix Invest</p>
        </motion.div>
        
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-gradient-to-br from-gray-50 to-white border-4 border-gray-300 rounded-2xl p-8 md:p-12">
            <div className="mb-8">
              <label className="block text-gray-700 font-medium mb-4">Investment Amount</label>
              <input
                type="range"
                min={minInvestment}
                max={maxInvestment}
                step="100000"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#D4AF37]"
                data-testid="calculator-slider"
              />
              <div className="flex justify-between items-center mt-4">
                <span className="text-gray-500 text-sm">{formatCurrency(minInvestment)}</span>
                <span className="text-3xl font-heading font-bold text-[#D4AF37]">{formatCurrency(amount)}</span>
                <span className="text-gray-500 text-sm">{formatCurrency(maxInvestment)}</span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-white border-2 border-[#D4AF37] rounded-xl p-6 text-center"
              >
                <p className="text-gray-600 mb-2">Monthly Return ({monthlyRate}%)</p>
                <p className="text-3xl font-heading font-bold text-[#D4AF37]">{formatCurrency(monthlyReturn)}</p>
                <p className="text-gray-500 text-sm mt-2">per month</p>
              </motion.div>
              
              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-[#D4AF37] rounded-xl p-6 text-center"
              >
                <p className="text-black/70 mb-2">{lumpSumMonths}-Month Return ({lumpSumRate}%)</p>
                <p className="text-3xl font-heading font-bold text-black">{formatCurrency(lumpSumReturn)}</p>
                <p className="text-black/70 text-sm mt-2">Total: {formatCurrency(totalAfterPeriod)}</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = ({ content, scrollToSection }) => {
  const data = content?.why_choose_us || {};
  const defaultFeatures = [
    { title: 'Capital Protection First', description: 'Your principal amount is always protected', icon: 'Shield' },
    { title: 'Client-First Advisory', description: 'Personalized investment guidance', icon: 'Handshake' },
    { title: 'Proven & Disciplined Models', description: 'Time-tested investment strategies', icon: 'Award' },
    { title: 'Monthly Income Focus', description: 'Regular returns to your account', icon: 'PiggyBank' },
    { title: 'Home Plans Benefits', description: 'Flexible plans for families', icon: 'Home' },
    { title: 'Legal Agreements', description: 'Transparent documentation', icon: 'FileCheck' },
  ];
  const features = data.features?.length > 0 ? data.features : defaultFeatures;

  return (
    <section id="why-choose-us" className="py-24 md:py-32 bg-white" data-testid="why-choose-us-section">
      <div className="nettrix-container">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="font-heading text-3xl md:text-5xl font-medium text-[#1A1A1A] mb-4">{data.section_title || 'Why Choose Us?'}</h2>
          <div className="w-16 h-1 bg-[#D4AF37] mx-auto"></div>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const IconComponent = getIcon(feature.icon);
            return (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="text-center group" data-testid={`feature-${index + 1}`}>
                <div className="w-20 h-20 mx-auto mb-4 bg-[#D4AF37] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform"><IconComponent className="w-10 h-10 text-white" /></div>
                <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mt-16 py-12 bg-gray-50 rounded-2xl">
          <p className="text-[#D4AF37] font-semibold text-lg mb-2">{data.cta_tagline || 'Safe. Trusted. Result-Driven.'}</p>
          <h3 className="font-heading text-2xl md:text-3xl font-medium text-[#1A1A1A] mb-6">{data.cta_title || 'Your growth starts with a single action'}</h3>
          <p className="text-gray-600 mb-6">{data.cta_description || 'Schedule your visit to our office.'}</p>
          <Button onClick={() => scrollToSection('contact')} className="bg-[#D4AF37] text-black hover:bg-[#F5C518] font-bold px-8 py-4" data-testid="book-consultation">{data.cta_button_text || 'Book Consultation'}</Button>
        </motion.div>
      </div>
    </section>
  );
};

// How to Start Section
const HowToStartSection = ({ content }) => {
  const data = content?.how_to_start || {};
  const defaultSteps = [
    { number: '01', title: 'Connect With Us', description: 'Fill out the contact form or reach us via WhatsApp.', icon: 'Phone' },
    { number: '02', title: 'Choose Your Plan', description: 'Select between 5% monthly or 30% lump sum.', icon: 'TrendingUp' },
    { number: '03', title: 'Transfer Funds', description: 'Complete documentation and transfer amount.', icon: 'Shield' },
    { number: '04', title: 'Receive Returns', description: 'Get monthly payouts or 30% after 6 months.', icon: 'CheckCircle' },
  ];
  const steps = data.steps?.length > 0 ? data.steps : defaultSteps;

  return (
    <section id="how-to-start" className="py-24 md:py-32 bg-gray-50" data-testid="how-to-start-section">
      <div className="nettrix-container">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="font-heading text-3xl md:text-5xl font-medium text-[#1A1A1A] mb-4">{data.section_title || 'How to Start?'}</h2>
          <div className="w-16 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">{data.section_subtitle || 'Follow these simple steps to begin your investment journey'}</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => {
            const IconComponent = getIcon(step.icon);
            return (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.15 }} className="relative group" data-testid={`start-step-${index + 1}`}>
                <div className="bg-white border border-gray-200 rounded-lg p-8 h-full hover:border-[#D4AF37] hover:shadow-lg transition-all duration-300">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="text-5xl font-heading font-bold text-[#D4AF37] opacity-40">{step.number}</span>
                    <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center"><IconComponent className="w-6 h-6 text-[#D4AF37]" /></div>
                  </div>
                  <h3 className="font-heading text-xl font-medium text-[#1A1A1A] mb-3">{step.title}</h3>
                  <p className="text-gray-600 text-base leading-relaxed">{step.description}</p>
                </div>
                {index < 3 && <div className="hidden lg:block absolute top-1/2 left-full w-8 h-0.5 bg-gradient-to-r from-[#D4AF37]/50 to-transparent" />}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Video Player Modal
const VideoPlayerModal = ({ isOpen, onClose, videoUrl, investorName }) => {
  if (!isOpen) return null;
  
  // Convert YouTube/Vimeo/Instagram URLs to embed format
  const getEmbedUrl = (url) => {
    if (!url) return null;
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`;
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
    // Instagram Reels/Posts
    const instaMatch = url.match(/instagram\.com\/(?:reel|p|reels)\/([a-zA-Z0-9_-]+)/);
    if (instaMatch) return `https://www.instagram.com/reel/${instaMatch[1]}/embed`;
    // Direct video URL
    return url;
  };
  
  const embedUrl = getEmbedUrl(videoUrl);
  const isDirectVideo = embedUrl === videoUrl && !embedUrl?.includes('youtube') && !embedUrl?.includes('vimeo') && !embedUrl?.includes('instagram');
  const isInstagram = videoUrl?.includes('instagram.com');

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
        data-testid="video-modal"
      >
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }} 
          animate={{ scale: 1, opacity: 1 }} 
          exit={{ scale: 0.9, opacity: 0 }}
          className={`relative bg-black rounded-xl overflow-hidden ${isInstagram ? 'w-full max-w-md aspect-[9/16]' : 'w-full max-w-4xl aspect-[9/16] md:aspect-video'}`}
          onClick={(e) => e.stopPropagation()}
        >
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
            data-testid="close-video-modal"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          
          {isDirectVideo ? (
            <video 
              src={embedUrl} 
              controls 
              autoPlay 
              className="w-full h-full object-contain"
              data-testid="video-player"
            >
              Your browser does not support the video tag.
            </video>
          ) : (
            <iframe
              src={embedUrl}
              title={`${investorName} Testimonial`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="video-iframe"
            />
          )}
          
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white font-medium text-lg">{investorName}</p>
            <p className="text-[#D4AF37] text-sm">Verified Investor</p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Testimonials Section
const TestimonialsSection = ({ testimonials }) => {
  const scrollRef = React.useRef(null);
  
  const displayTestimonials = testimonials.length > 0 ? testimonials : [
    { 
      id: '1', 
      investor_name: 'Rajesh Sharma', 
      designation: 'Business Owner',
      location: 'Mumbai',
      testimonial_text: 'I was skeptical at first, but Nettrix has consistently delivered on their promises. The 5% monthly returns have been credited to my account without fail for the past 8 months.',
      rating: 5,
      investment_amount: '₹10,00,000',
      image_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'
    },
    { 
      id: '2', 
      investor_name: 'Priya Patel', 
      designation: 'IT Professional',
      location: 'Bangalore',
      testimonial_text: 'The transparency and professionalism of the Nettrix team is commendable. All documentation is proper and returns are always on time. Highly recommended!',
      rating: 5,
      investment_amount: '₹5,00,000',
      image_url: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face'
    },
    { 
      id: '3', 
      investor_name: 'Amit Kumar', 
      designation: 'Retired Government Officer',
      location: 'Delhi',
      testimonial_text: 'After retirement, I was looking for safe investment options. Nettrix provides exactly that - safety with good returns. My capital is secure and I receive regular income.',
      rating: 5,
      investment_amount: '₹15,00,000',
      image_url: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face'
    },
    { 
      id: '4', 
      investor_name: 'Sneha Gupta', 
      designation: 'Doctor',
      location: 'Pune',
      testimonial_text: 'As a busy professional, I needed a hassle-free investment. The team handles everything professionally. The 30% return after 6 months was exactly as promised.',
      rating: 5,
      investment_amount: '₹8,00,000',
      image_url: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face'
    },
    { 
      id: '5', 
      investor_name: 'Vikram Singh', 
      designation: 'Chartered Accountant',
      location: 'Ahmedabad',
      testimonial_text: 'Being a CA, I thoroughly checked their financials and documentation. Everything is legitimate and transparent. The returns are excellent compared to FDs.',
      rating: 5,
      investment_amount: '₹12,00,000',
      image_url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face'
    },
    { 
      id: '6', 
      investor_name: 'Meera Reddy', 
      designation: 'Entrepreneur',
      location: 'Hyderabad',
      testimonial_text: 'I have been investing with Nettrix for over a year now. The consistent returns have helped me plan my business expansion confidently.',
      rating: 5,
      investment_amount: '₹20,00,000',
      image_url: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop&crop=face'
    },
    { 
      id: '7', 
      investor_name: 'Suresh Menon', 
      designation: 'NRI - Software Engineer',
      location: 'Dubai',
      testimonial_text: 'As an NRI, finding trustworthy investment options in India was challenging. Nettrix made it easy with their professional approach and timely communication.',
      rating: 5,
      investment_amount: '₹25,00,000',
      image_url: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&h=150&fit=crop&crop=face'
    },
    { 
      id: '8', 
      investor_name: 'Anita Desai', 
      designation: 'School Principal',
      location: 'Jaipur',
      testimonial_text: 'The monthly income from my investment helps me support my family better. The team is very patient in explaining everything clearly.',
      rating: 5,
      investment_amount: '₹6,00,000',
      image_url: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face'
    },
    { 
      id: '9', 
      investor_name: 'Rohit Agarwal', 
      designation: 'Real Estate Developer',
      location: 'Kolkata',
      testimonial_text: 'I have multiple investments with Nettrix. Their structured approach and guaranteed returns make them stand out from other options.',
      rating: 5,
      investment_amount: '₹50,00,000',
      image_url: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop&crop=face'
    },
    { 
      id: '10', 
      investor_name: 'Kavita Joshi', 
      designation: 'Homemaker',
      location: 'Chennai',
      testimonial_text: 'Started with a small amount to test, and now I have increased my investment significantly. The peace of mind is priceless.',
      rating: 5,
      investment_amount: '₹3,00,000',
      image_url: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&h=150&fit=crop&crop=face'
    },
  ];

  const scroll = (direction) => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const scrollAmount = 400;
      
      if (direction === 'left') {
        // If at the start, jump to the end
        if (container.scrollLeft <= 0) {
          container.scrollTo({ left: container.scrollWidth, behavior: 'smooth' });
        } else {
          container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        }
      } else {
        // If at the end, jump to the start
        if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 10) {
          container.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
      }
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star key={i} className={`w-4 h-4 ${i < rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-gray-300'}`} />
    ));
  };

  return (
    <section id="testimonials" className="py-24 md:py-32 bg-gray-50" data-testid="testimonials-section">
      <div className="nettrix-container">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-5xl font-medium text-[#1A1A1A] mb-4">What Our Investors Say</h2>
          <div className="w-16 h-1 bg-[#D4AF37] mx-auto"></div>
        </motion.div>
        <div className="relative">
          <button onClick={() => scroll('left')} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 p-3 rounded-full hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 md:-translate-x-1/2 translate-x-2 flex shadow-md" data-testid="scroll-left"><ChevronLeft className="w-6 h-6 text-[#1A1A1A]" /></button>
          <button onClick={() => scroll('right')} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 p-3 rounded-full hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 md:translate-x-1/2 -translate-x-2 flex shadow-md" data-testid="scroll-right"><ChevronRight className="w-6 h-6 text-[#1A1A1A]" /></button>
          <div ref={scrollRef} className="flex gap-6 overflow-x-auto pb-4 px-12 md:px-4 scroll-smooth" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
            {displayTestimonials.map((t, i) => (
              <motion.div 
                key={t.id} 
                initial={{ opacity: 0, y: 30 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }} 
                transition={{ delay: i * 0.1 }} 
                className="flex-shrink-0 w-[350px] bg-white border-2 border-gray-100 rounded-2xl p-6 hover:border-[#D4AF37] hover:shadow-xl transition-all"
                data-testid={`testimonial-card-${t.id}`}
              >
                {/* Quote Icon */}
                <div className="mb-4">
                  <svg className="w-10 h-10 text-[#D4AF37] opacity-50" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                  </svg>
                </div>
                
                {/* Testimonial Text */}
                <p className="text-gray-600 text-base leading-relaxed mb-6 min-h-[100px]">
                  "{t.testimonial_text || 'Great experience with Nettrix Invest!'}"
                </p>
                
                {/* Rating */}
                <div className="flex gap-1 mb-4">
                  {renderStars(t.rating || 5)}
                </div>
                
                {/* Investor Info */}
                <div className="flex items-center gap-4 pt-4 border-t border-gray-100">
                  <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-[#D4AF37] flex-shrink-0">
                    <img 
                      src={t.image_url || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'} 
                      alt={t.investor_name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'; }}
                    />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-[#1A1A1A]">{t.investor_name}</p>
                    <p className="text-sm text-gray-500">{t.designation || 'Investor'}{t.location && `, ${t.location}`}</p>
                    {t.investment_amount && (
                      <p className="text-xs text-[#D4AF37] font-semibold mt-1">Invested: {t.investment_amount}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    <div className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                      <Shield className="w-3 h-3" /> Verified
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = ({ settings }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', investment_amount: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...formData, investment_amount: formData.investment_amount ? Number(formData.investment_amount) : null });
      toast.success('Thank you! We will contact you shortly.');
      setFormData({ name: '', email: '', phone: '', investment_amount: '', message: '' });
    } catch (error) { toast.error('Something went wrong.'); }
    finally { setLoading(false); }
  };

  // Clean WhatsApp number (remove spaces, +, etc. - keep only digits)
  const cleanWhatsappNumber = (settings?.whatsapp_number || '919999999999').replace(/[^0-9]/g, '');
  const whatsappLink = `https://wa.me/${cleanWhatsappNumber}?text=${encodeURIComponent(settings?.whatsapp_message || "Hi, I'm interested in investing.")}`;

  return (
    <section id="contact" className="py-24 md:py-32 bg-gray-50" data-testid="contact-section">
      <div className="nettrix-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-3xl md:text-5xl font-medium text-[#1A1A1A] mb-4">Ready to Invest?</h2>
            <div className="w-16 h-1 bg-[#D4AF37] mb-6"></div>
            <p className="text-gray-600 text-lg leading-relaxed mb-10">Connect with our investment advisors to start your journey towards 30% returns.</p>
            <div className="space-y-6">
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 bg-[#25D366]/10 border border-[#25D366]/30 rounded-lg hover:border-[#25D366] group" data-testid="whatsapp-contact">
                <div className="w-12 h-12 rounded-full bg-[#25D366]/20 flex items-center justify-center text-[#25D366]"><WhatsAppIcon /></div>
                <div><p className="text-[#1A1A1A] font-semibold">WhatsApp Us</p><p className="text-gray-500 text-sm">Quick response</p></div>
              </a>
              <div className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-lg"><div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center"><Phone className="w-5 h-5 text-[#D4AF37]" /></div><div><p className="text-[#1A1A1A] font-semibold">{settings?.contact_phone || '+91 99999 99999'}</p><p className="text-gray-500 text-sm">Mon - Sat, 10AM - 7PM</p></div></div>
              <div className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-lg"><div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center"><Mail className="w-5 h-5 text-[#D4AF37]" /></div><div><p className="text-[#1A1A1A] font-semibold">{settings?.contact_email || 'invest@nettrix.com'}</p><p className="text-gray-500 text-sm">Email us anytime</p></div></div>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm" data-testid="contact-form">
              <h3 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-6">Request a Callback</h3>
              <div className="space-y-5">
                <Input type="text" placeholder="Your Name *" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="bg-white border-gray-300 text-[#1A1A1A] h-12" data-testid="input-name" />
                <Input type="email" placeholder="Email Address *" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="bg-white border-gray-300 text-[#1A1A1A] h-12" data-testid="input-email" />
                <Input type="tel" placeholder="Phone Number *" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} required className="bg-white border-gray-300 text-[#1A1A1A] h-12" data-testid="input-phone" />
                <Input type="number" placeholder="Investment Amount (₹)" value={formData.investment_amount} onChange={(e) => setFormData({...formData, investment_amount: e.target.value})} className="bg-white border-gray-300 text-[#1A1A1A] h-12" data-testid="input-amount" />
                <Textarea placeholder="Your Message (Optional)" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} rows={4} className="bg-white border-gray-300 text-[#1A1A1A]" data-testid="input-message" />
                <Button type="submit" disabled={loading} className="w-full bg-[#D4AF37] text-black hover:bg-[#F5C518] font-bold py-6" data-testid="submit-form">{loading ? 'Sending...' : 'REQUEST CALLBACK'}<Send className="ml-2 w-5 h-5" /></Button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = ({ settings, content, socialLinks }) => {
  const footer = content?.footer || {};
  
  // Get social link URLs
  const getLink = (platform) => socialLinks?.find(l => l.platform?.toLowerCase() === platform)?.url || '';
  
  return (
    <footer className="bg-white border-t border-gray-200" data-testid="footer">
      <div className="nettrix-container py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Logo & Description */}
          <div className="md:col-span-2">
            <img src="https://customer-assets.emergentagent.com/job_552d4860-4146-493e-8e68-213c46e43a66/artifacts/eqpwekmt_Nettrix%20Logo.png" alt="Nettrix Invest" className="h-20 w-auto mb-6" />
            <p className="text-gray-600 text-sm leading-relaxed max-w-md">{footer.description || "As India's trusted investment consultancy, we create simple, safe and effective investment plans. Our approach delivers consistent results without market stress."}</p>
            
            {/* Social Icons */}
            <div className="flex gap-4 mt-6">
              {getLink('instagram') && (
                <a href={getLink('instagram')} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-100 hover:bg-[#D4AF37] rounded-full flex items-center justify-center transition-all group">
                  <InstagramIcon className="w-5 h-5 text-[#1A1A1A] group-hover:text-white" />
                </a>
              )}
              {getLink('linkedin') && (
                <a href={getLink('linkedin')} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-100 hover:bg-[#D4AF37] rounded-full flex items-center justify-center transition-all group">
                  <svg className="w-5 h-5 text-[#1A1A1A] group-hover:text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
              )}
              <a href={`https://wa.me/${(settings?.whatsapp_number || '919999999999').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-100 hover:bg-[#25D366] rounded-full flex items-center justify-center transition-all group">
                <WhatsAppIcon className="w-5 h-5 text-[#1A1A1A] group-hover:text-white" />
              </a>
            </div>
          </div>
          
          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-6 flex items-center gap-2">
              <div className="w-8 h-0.5 bg-[#D4AF37]"></div>
              Quick Links
            </h4>
            <div className="flex flex-col gap-3">
              <a href="#hero" className="text-gray-600 hover:text-[#D4AF37] text-sm transition-colors">Home</a>
              <a href="#how-it-works" className="text-gray-600 hover:text-[#D4AF37] text-sm transition-colors">How It Works</a>
              <a href="#about-us" className="text-gray-600 hover:text-[#D4AF37] text-sm transition-colors">About Us</a>
              <a href="#our-plans" className="text-gray-600 hover:text-[#D4AF37] text-sm transition-colors">Our Plans</a>
              <a href="#contact" className="text-gray-600 hover:text-[#D4AF37] text-sm transition-colors">Contact</a>
            </div>
          </div>
          
          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-6 flex items-center gap-2">
              <div className="w-8 h-0.5 bg-[#D4AF37]"></div>
              Contact Us
            </h4>
            <div className="space-y-4">
              <a href={`mailto:${settings?.contact_email || 'invest@nettrixinvest.com'}`} className="flex items-center gap-3 text-gray-600 hover:text-[#D4AF37] text-sm transition-colors">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <span>{settings?.contact_email || 'invest@nettrixinvest.com'}</span>
              </a>
              <a href={`tel:${(settings?.whatsapp_number || '').replace(/[^0-9+]/g, '')}`} className="flex items-center gap-3 text-gray-600 hover:text-[#D4AF37] text-sm transition-colors">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <span>{settings?.whatsapp_number || '+91 7899608057'}</span>
              </a>
              <div className="flex items-start gap-3 text-gray-600 text-sm">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <span className="leading-relaxed">{settings?.address || 'Bengaluru, Karnataka, India'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Bar */}
      <div className="border-t border-gray-200 bg-gray-50">
        <div className="nettrix-container py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">{footer.copyright_text || '© 2025 Nettrix Marketing Pvt. Ltd. All Rights Reserved.'}</p>
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <span>Trusted by investors across India</span>
            <div className="flex -space-x-2">
              <div className="w-6 h-6 bg-[#D4AF37] rounded-full border-2 border-white"></div>
              <div className="w-6 h-6 bg-gray-400 rounded-full border-2 border-white"></div>
              <div className="w-6 h-6 bg-gray-500 rounded-full border-2 border-white"></div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppFloat = ({ settings }) => {
  const cleanNumber = (settings?.whatsapp_number || '919999999999').replace(/[^0-9]/g, '');
  return (
    <a href={`https://wa.me/${cleanNumber}?text=${encodeURIComponent(settings?.whatsapp_message || "Hi, I'm interested.")}`} target="_blank" rel="noopener noreferrer" className="whatsapp-float w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform text-white" data-testid="whatsapp-float">
      <WhatsAppIcon />
    </a>
  );
};

// Main Landing Page Component
const LandingPage = () => {
  const [urgencyData, setUrgencyData] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [settings, setSettings] = useState(null);
  const [content, setContent] = useState(null);
  const [socialLinks, setSocialLinks] = useState([]);
  const [calculatorSettings, setCalculatorSettings] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [urgencyRes, testimonialsRes, settingsRes, contentRes, socialRes, calcRes] = await Promise.all([
          axios.get(`${API}/urgency-meter`).catch(() => ({ data: null })),
          axios.get(`${API}/testimonials`).catch(() => ({ data: [] })),
          axios.get(`${API}/settings`).catch(() => ({ data: null })),
          axios.get(`${API}/content`).catch(() => ({ data: null })),
          axios.get(`${API}/social-links`).catch(() => ({ data: [] })),
          axios.get(`${API}/calculator-settings`).catch(() => ({ data: null }))
        ]);
        setUrgencyData(urgencyRes.data);
        setTestimonials(testimonialsRes.data || []);
        setSettings(settingsRes.data);
        setContent(contentRes.data);
        setSocialLinks(socialRes.data || []);
        setCalculatorSettings(calcRes.data);
      } catch (error) { console.error('Error fetching data:', error); }
    };
    fetchData();
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 112;
      window.scrollTo({ top: element.getBoundingClientRect().top + window.pageYOffset - offset, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation scrollToSection={scrollToSection} />
      <HeroSection urgencyData={urgencyData} scrollToSection={scrollToSection} content={content} />
      <HowItWorksSection content={content} />
      <AboutUsSection content={content} />
      <OurPlansSection content={content} />
      <InvestmentCalculator settings={calculatorSettings} />
      <WhyChooseUsSection content={content} scrollToSection={scrollToSection} />
      <HowToStartSection content={content} />
      <TestimonialsSection testimonials={testimonials} />
      <ContactSection settings={settings} />
      <Footer settings={settings} content={content} socialLinks={socialLinks} />
      <WhatsAppFloat settings={settings} />
    </div>
  );
};

export default LandingPage;
